import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { createRoot } from "react-dom/client";
import { buttonRecipe } from "./components/core/basic/Button";
import commonEn from "./locales/en/common.json";
import { tokenLogin } from "./modules/auth/tokens/tokenLogin";
import { getAuthTokens } from "./modules/auth/tokens/tokens";
import { setErrors } from "./redux/reducers/errorReducers";
import { logoutUser } from "./redux/reducers/userReducer";
import { store } from "./redux/store";
import { apiErrorFingerprint } from "./utils/apiErrorFingerprint";
import { isNetworkError } from "./utils/isNetworkError";
import { describeZodiosValidationFailure } from "./utils/responseDiagnostics";
import { VStack } from "../styled-system/jsx";

import "./i18n";
import "./config/highcharts";
import App from "./App";
import { router } from "./router";

import "./index.css";
import "./assets/sass/light-bootstrap-dashboard-pro-react.scss";
import "./assets/sass/theme_overrides.scss";
import { getEnvironment } from "./utils/getUrlPrefix";

const NETWORK_ERROR_FINGERPRINT = "network-error";

// Scoped to known ad/analytics hostnames, not "Failed to fetch" generally — we never call these
// domains ourselves, so a match is always a GTM beacon blocked by an ad blocker.
const AD_TRACKER_FETCH_FAILURE =
  /^Failed to fetch \([\w.-]*\.(?:google(?:-analytics)?|googletagmanager|googlesyndication|doubleclick|facebook|linkedin|licdn)\.(?:com|net)\)$/;

Sentry.init({
  dsn:
    import.meta.env.PROD ?
      "https://9514d9f62a744b1cbc89b3e83bfd672d@o944803.ingest.sentry.io/5893305"
    : import.meta.env.VITE_SENTRY_DSN,
  environment: getEnvironment(),
  sendDefaultPii: true,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router), Sentry.replayIntegration()],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  _experiments: { enableLogs: true },
  ignoreErrors: ["Request failed with status code 401", AD_TRACKER_FETCH_FAILURE],
  beforeSend(event, hint) {
    const error = hint.originalException;
    const zodiosValidationFailure = describeZodiosValidationFailure(error);
    if (zodiosValidationFailure !== null) {
      event.contexts = { ...event.contexts, zodiosResponse: zodiosValidationFailure };
    }
    if (!isNetworkError(error)) {
      const fingerprint = apiErrorFingerprint(event, error);
      return fingerprint === undefined ? event : { ...event, fingerprint };
    }
    const requestUrl = isAxiosError(error) ? error.config?.url : undefined;
    return {
      ...event,
      level: "warning",
      fingerprint: [NETWORK_ERROR_FINGERPRINT],
      tags: requestUrl === undefined ? event.tags : { ...event.tags, requestUrl },
    };
  },
});

const root = createRoot(document.getElementById("root")!);

async function enableMocking() {
  const mockMode = import.meta.env.VITE_ENABLE_MOCKS;
  if (["ALL", "CLEAN_SLATE"].includes(mockMode)) {
    const { worker } = await import("../mocks/browser");
    const { http, passthrough } = await import("msw");
    if (mockMode === "CLEAN_SLATE") {
      worker.resetHandlers(http.all("/dummy", () => passthrough()));
    }
    return await worker.start({ onUnhandledRequest: mockMode === "ALL" ? "error" : "bypass" });
  }
}

const BOOT_ATTEMPTS = 3;
const BOOT_RETRY_DELAY_MS = 500;

async function bootstrapSession() {
  const tokens = getAuthTokens();

  for (let attempt = 1; attempt <= BOOT_ATTEMPTS; attempt++) {
    try {
      await tokenLogin(tokens, store.dispatch);
      return;
    } catch (err) {
      if (!isNetworkError(err) || attempt === BOOT_ATTEMPTS) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_DELAY_MS * attempt));
    }
  }
}

function renderConnectionError() {
  root.render(
    <VStack>
      <h3>{commonEn["An error occurred."]}</h3>
      <a href="/" className={buttonRecipe()}>
        {commonEn.actions.Reload}
      </a>
    </VStack>,
  );
}

void enableMocking().then(() => {
  bootstrapSession().catch((err: unknown) => {
    if (isNetworkError(err)) {
      Sentry.captureMessage("Session bootstrap unreachable on boot", {
        level: "warning",
        extra: {
          attempts: BOOT_ATTEMPTS,
          code: isAxiosError(err) ? err.code : undefined,
          url: isAxiosError(err) ? err.config?.url : undefined,
          navigatorOnLine: navigator.onLine,
        },
      });
      renderConnectionError();
      return;
    }
    const isUnauthorized = isAxiosError(err) && err.response?.status === 401;
    if (!isUnauthorized) {
      Sentry.captureException(err);
    }

    store.dispatch(setErrors({ code: "SESSION_EXPIRED" }));
    store.dispatch(logoutUser());
  });

  root.render(<App />);
});
